import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Flag, Users, ShieldCheck, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const LABELS = {
  post: "post", comment: "comment", repost: "repost",
  personal: "profile", business: "business", capital: "investor profile",
  event: "event", resource: "resource", group: "group",
};

/** One Report entry point, two destinations (Phase 7 §3). The destination choice only
 * appears for content genuinely scoped to a Group, and the server re-derives it, so this is
 * the reporter's choice — never the authority. */
function DestinationCard({ active, onClick, icon: Icon, title, description, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
        active ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary/50"
      }`}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-primary" : "text-muted-foreground"}`} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-sm font-[560] text-foreground">
          {title}
          {active ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
        </span>
        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{description}</span>
      </span>
    </button>
  );
}

/**
 * Controlled report modal. Lets a member flag a post/comment/profile/event/resource
 * for moderation. Reasons are scoped to the content type. Reports are anonymous to the
 * author. On success calls `onReported` (used to optimistically hide content from the
 * reporter's view).
 */
export function ReportDialog({ open, onOpenChange, contentType, contentId, onReported }) {
  const [reasons, setReasons] = useState([]);
  const [group, setGroup] = useState(null);
  const [destination, setDestination] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const label = LABELS[contentType] || "content";

  // Reasons + the available destinations depend on the content, so reload on every open.
  useEffect(() => {
    if (open) {
      setReasons([]);
      setGroup(null);
      setDestination("");
      const params = new URLSearchParams({ content_type: contentType });
      if (contentId) params.set("content_id", contentId);
      api.get(`/reports/reasons?${params.toString()}`)
        .then(({ data }) => {
          setReasons(data.reasons || []);
          setGroup(data.group || null);
          if (!data.group) setDestination("platform");
        }).catch(() => {});
    }
  }, [open, contentType, contentId]);

  const change = (o) => {
    if (!o) { setReason(""); setDetails(""); }
    onOpenChange(o);
  };

  const submit = async () => {
    if (!destination) { toast.error("Choose who should review this."); return; }
    if (!reason) { toast.error("Please choose a reason for reporting."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/reports", {
        content_type: contentType, content_id: contentId, reason, details: details.trim(),
        destination,
      });
      toast.success(data?.message || "Thanks — our team will review this.");
      setReason("");
      setDetails("");
      onOpenChange(false);
      if (onReported) onReported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit report");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>Report this {label}</DialogTitle>
          <DialogDescription>
            Tell us what&apos;s wrong. Your report is anonymous to the author.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          {group ? (
            <div className="space-y-2" data-testid="report-destination-choice">
              <div className="text-xs font-[560] uppercase tracking-wide text-muted-foreground">
                Who should review this?
              </div>
              <DestinationCard
                active={destination === "group"}
                onClick={() => setDestination("group")}
                icon={Users}
                title={`Report to ${group.display_name || "the group"} organizers`}
                description="Report a violation of this group's rules. Reviewed by the group's Owners and Admins."
                testId="report-destination-group"
              />
              <DestinationCard
                active={destination === "platform"}
                onClick={() => setDestination("platform")}
                icon={ShieldCheck}
                title="Report to Startup Otter"
                description="Report harassment, threats, scams, or violations of Startup Otter's platform rules."
                testId="report-destination-platform"
              />
            </div>
          ) : null}
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason-trigger"><SelectValue placeholder="Choose a reason…" /></SelectTrigger>
            <SelectContent>
              {reasons.map((r) => (
                <SelectItem key={r.value} value={r.value} data-testid={`report-reason-${r.value}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={2000}
            placeholder="Add any details (optional)…"
            rows={3}
            data-testid="report-details"
          />
          {group && destination === "group" ? (
            <p className="text-xs text-muted-foreground" data-testid="report-destination-note">
              Serious safety concerns are always reviewed by Startup Otter as well.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="report-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || !reason || !destination}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="report-submit"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flag className="mr-2 h-4 w-4" />} Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
